import { AxiosRequestConfig } from "axios";
import { API_ENDPOINT, AUTH_TOKEN_KEY } from "../constants";
import StorageService from "./StorageService";

type AppSurface = "web" | "pwa" | "extension";
type AnalyticsParam = string | number | boolean;
type AnalyticsParams = Record<string, AnalyticsParam | undefined>;

type QueuedEvent = {
  name: string;
  params: Record<string, AnalyticsParam>;
  queuedAt: number;
};

type StoredSession = {
  id: string;
  expiresAt: number;
};

type RequestOutcome = "success" | "failure";

const ANALYTICS_CLIENT_ID_KEY = "prismancy_analytics_client_id";
const ANALYTICS_SESSION_KEY = "prismancy_analytics_session";
const ANALYTICS_SESSION_TTL_MS = 30 * 60 * 1000;
const ANALYTICS_EVENT_TTL_MS = 2 * 60 * 1000;
const ANALYTICS_FLUSH_DELAY_MS = 1500;
const ANALYTICS_BATCH_SIZE = 25;
const ENABLED = import.meta.env.VITE_APP_ANALYTICS_ENABLED === "true";
const EVENT_NAME_PATTERN = /^[a-z][a-z0-9_]{0,39}$/;
const DYNAMIC_PATH_SEGMENT =
  /^(?:[0-9a-f]{8}-[0-9a-f-]{27,}|[0-9a-f]{20,}|\d{6,}|[^/]*@[^/]*)$/i;

const ACTION_ROUTES: Array<{
  method: string;
  pattern: RegExp;
  eventName: string;
}> = [
  { method: "POST", pattern: /^\/login\/?$/, eventName: "login" },
  { method: "POST", pattern: /^\/register\/?$/, eventName: "sign_up" },
  {
    method: "POST",
    pattern: /^\/verify\/otp\/?$/,
    eventName: "account_verified",
  },
  {
    method: "POST",
    pattern: /^\/forgot-password\/?$/,
    eventName: "password_reset_requested",
  },
  {
    method: "POST",
    pattern: /^\/reset-password\/?$/,
    eventName: "password_reset",
  },
  { method: "PUT", pattern: /^\/profile\/?$/, eventName: "profile_updated" },
  { method: "POST", pattern: /^\/logout\/?$/, eventName: "logout" },
  {
    method: "POST",
    pattern: /^\/workspace\/create-workspace\/?$/,
    eventName: "workspace_created",
  },
  {
    method: "PUT",
    pattern: /^\/workspace\/update-workspace\/:id\/?$/,
    eventName: "workspace_updated",
  },
  {
    method: "DELETE",
    pattern: /^\/workspace\/delete-workspace\/:id\/?$/,
    eventName: "workspace_deleted",
  },
  {
    method: "POST",
    pattern: /^\/workspace\/set-user-workspace\/:id\/?$/,
    eventName: "workspace_selected",
  },
  {
    method: "POST",
    pattern: /^\/workspace\/invite-to-workspace\/?$/,
    eventName: "workspace_invite_sent",
  },
  {
    method: "DELETE",
    pattern: /^\/workspace\/remove-team-workspace\/?$/,
    eventName: "workspace_member_removed",
  },
  {
    method: "DELETE",
    pattern: /^\/workspace\/cancel-invite-workspace\/?$/,
    eventName: "workspace_invite_cancelled",
  },
  {
    method: "PUT",
    pattern: /^\/workspace\/update-team-workspace\/?$/,
    eventName: "workspace_member_updated",
  },
  {
    method: "POST",
    pattern: /^\/workspace\/add-domain\/?$/,
    eventName: "domain_added",
  },
  {
    method: "PUT",
    pattern: /^\/workspace\/verify-domain\/:id\/?$/,
    eventName: "domain_verified",
  },
  {
    method: "DELETE",
    pattern: /^\/workspace\/remove-domain\/:id\/?$/,
    eventName: "domain_removed",
  },
  {
    method: "POST",
    pattern: /^\/workspace\/settings\/ga4\/:id\/?$/,
    eventName: "ga4_settings_updated",
  },
  {
    method: "POST",
    pattern: /^\/workspace\/settings\/shopify\/test\/?$/,
    eventName: "shopify_connection_tested",
  },
  {
    method: "POST",
    pattern: /^\/workspace\/settings\/shopify\/sync\/:id\/?$/,
    eventName: "shopify_sync_started",
  },
  {
    method: "PUT",
    pattern: /^\/workspace\/settings\/shopify\/:id\/?$/,
    eventName: "shopify_settings_updated",
  },
  {
    method: "PUT",
    pattern: /^\/workspace\/settings\/shopify\/headless\/:id\/?$/,
    eventName: "shopify_headless_updated",
  },
  {
    method: "POST",
    pattern: /^\/experiment\/experiment\/?$/,
    eventName: "experiment_created",
  },
  {
    method: "PUT",
    pattern: /^\/experiment\/experiment\/:id\/?$/,
    eventName: "experiment_updated",
  },
  {
    method: "PUT",
    pattern: /^\/experiment\/experiment-start\/:id\/?$/,
    eventName: "experiment_started",
  },
  {
    method: "PUT",
    pattern: /^\/experiment\/experiment-end\/:id\/?$/,
    eventName: "experiment_ended",
  },
  {
    method: "DELETE",
    pattern: /^\/experiment\/experiment\/:id\/?$/,
    eventName: "experiment_deleted",
  },
  {
    method: "POST",
    pattern: /^\/experiment\/experiment\/:id\/duplicate\/?$/,
    eventName: "experiment_duplicated",
  },
  {
    method: "POST",
    pattern: /^\/experiment\/experiment\/:id\/variant\/:id\/publish\/?$/,
    eventName: "variant_published",
  },
  {
    method: "POST",
    pattern: /^\/experiment\/variant\/?$/,
    eventName: "variant_created",
  },
  {
    method: "PUT",
    pattern: /^\/experiment\/variant\/:id\/?$/,
    eventName: "variant_updated",
  },
  {
    method: "POST",
    pattern: /^\/experiment\/variant\/duplicate\/:id\/?$/,
    eventName: "variant_duplicated",
  },
  {
    method: "PUT",
    pattern: /^\/experiment\/variant\/control\/:id\/?$/,
    eventName: "variant_control_set",
  },
  {
    method: "DELETE",
    pattern: /^\/experiment\/variant\/:id\/?$/,
    eventName: "variant_deleted",
  },
  {
    method: "PUT",
    pattern: /^\/experiment\/update-variants-weight\/:id\/?$/,
    eventName: "variant_weights_updated",
  },
  {
    method: "POST",
    pattern: /^\/experiment\/changes\/?$/,
    eventName: "change_created",
  },
  {
    method: "PUT",
    pattern: /^\/experiment\/changes\/:id\/?$/,
    eventName: "change_updated",
  },
  {
    method: "DELETE",
    pattern: /^\/experiment\/changes\/:id\/?$/,
    eventName: "change_deleted",
  },
  {
    method: "GET",
    pattern: /^\/experiment\/experiment\/:id\/variant\/:id\/preview-link\/?$/,
    eventName: "preview_opened",
  },
  {
    method: "POST",
    pattern: /^\/experiment\/preview\/:id\/prepare\/?$/,
    eventName: "preview_prepared",
  },
  {
    method: "POST",
    pattern: /^\/experiment\/preview\/:id\/invalidate\/?$/,
    eventName: "preview_invalidated",
  },
  {
    method: "POST",
    pattern: /^\/billing\/:id\/checkout\/?$/,
    eventName: "billing_checkout_started",
  },
  {
    method: "GET",
    pattern: /^\/billing\/:id\/portal\/?$/,
    eventName: "billing_portal_opened",
  },
  {
    method: "PUT",
    pattern: /^\/billing\/:id\/extra-credits\/?$/,
    eventName: "extra_credits_updated",
  },
];

let queue: QueuedEvent[] = [];
let flushTimer: number | null = null;
let flushPromise: Promise<void> | null = null;

export const isExtensionRuntime = (): boolean =>
  typeof chrome !== "undefined" && Boolean(chrome.runtime?.id);

const getAppSurface = (): AppSurface => {
  if (isExtensionRuntime()) {
    return "extension";
  }

  const isStandalone =
    window.matchMedia?.("(display-mode: standalone)").matches ||
    Boolean(
      (window.navigator as Navigator & { standalone?: boolean }).standalone,
    );
  return isStandalone ? "pwa" : "web";
};

const randomId = (): string => {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const getClientId = async (): Promise<string> => {
  const stored = await StorageService.getStorageItem<string>(
    ANALYTICS_CLIENT_ID_KEY,
  );
  if (stored) {
    return stored;
  }

  const clientId = `${randomId().replace(/-/g, "").slice(0, 16)}.${Date.now()}`;
  await StorageService.setStorageItem(ANALYTICS_CLIENT_ID_KEY, clientId);
  return clientId;
};

const getSessionId = async (): Promise<string> => {
  const now = Date.now();
  const stored = await StorageService.getStorageItem<StoredSession>(
    ANALYTICS_SESSION_KEY,
  );
  const session =
    stored && stored.expiresAt > now
      ? { ...stored, expiresAt: now + ANALYTICS_SESSION_TTL_MS }
      : { id: String(now), expiresAt: now + ANALYTICS_SESSION_TTL_MS };

  await StorageService.setStorageItem(ANALYTICS_SESSION_KEY, session);
  return session.id;
};

export const normalizeAnalyticsPath = (value: string): string => {
  let pathname = value;
  try {
    pathname = new URL(value, window.location.origin).pathname;
  } catch {
    pathname = value.split(/[?#]/, 1)[0];
  }

  const normalized = pathname
    .split("/")
    .map((segment) => {
      try {
        return DYNAMIC_PATH_SEGMENT.test(decodeURIComponent(segment))
          ? ":id"
          : segment;
      } catch {
        return ":id";
      }
    })
    .join("/");

  return normalized.startsWith("/") ? normalized : `/${normalized}`;
};

const sanitizeParams = (
  params: AnalyticsParams,
): Record<string, AnalyticsParam> =>
  Object.entries(params).reduce<Record<string, AnalyticsParam>>(
    (result, [name, value]) => {
      if (
        value === undefined ||
        !["string", "number", "boolean"].includes(typeof value)
      ) {
        return result;
      }
      if (typeof value === "number" && !Number.isFinite(value)) {
        return result;
      }

      result[name] = typeof value === "string" ? value.slice(0, 100) : value;
      return result;
    },
    {},
  );

const scheduleFlush = (delay = ANALYTICS_FLUSH_DELAY_MS): void => {
  if (flushTimer !== null || typeof window === "undefined") {
    return;
  }

  flushTimer = window.setTimeout(() => {
    flushTimer = null;
    void flushAnalytics();
  }, delay);
};

const getAppVersion = (): string => {
  if (isExtensionRuntime()) {
    return chrome.runtime.getManifest().version;
  }
  return import.meta.env.VITE_BUILD_NUMBER || "web";
};

export const trackAppEvent = (
  name: string,
  params: AnalyticsParams = {},
): void => {
  if (!ENABLED || !EVENT_NAME_PATTERN.test(name)) {
    return;
  }

  queue.push({
    name,
    params: sanitizeParams({
      ...params,
      app_version: getAppVersion(),
      environment: import.meta.env.MODE,
    }),
    queuedAt: Date.now(),
  });
  queue = queue.slice(-ANALYTICS_BATCH_SIZE * 2);
  scheduleFlush(
    queue.length >= ANALYTICS_BATCH_SIZE ? 0 : ANALYTICS_FLUSH_DELAY_MS,
  );
};

export const flushAnalytics = async (): Promise<void> => {
  if (!ENABLED || flushPromise || queue.length === 0) {
    return flushPromise || Promise.resolve();
  }

  flushPromise = (async () => {
    const now = Date.now();
    queue = queue.filter(
      (event) => now - event.queuedAt <= ANALYTICS_EVENT_TTL_MS,
    );
    if (queue.length === 0) {
      return;
    }

    const token = await StorageService.getStorageItem<string>(AUTH_TOKEN_KEY);
    if (!token) {
      scheduleFlush(5000);
      return;
    }

    const events = queue.splice(0, ANALYTICS_BATCH_SIZE);
    const [clientId, sessionId] = await Promise.all([
      getClientId(),
      getSessionId(),
    ]);

    try {
      await fetch(
        `${API_ENDPOINT.replace(/\/$/, "")}/workspace/app-analytics/events`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            client_id: clientId,
            session_id: sessionId,
            app_surface: getAppSurface(),
            events: events.map(({ name, params }) => ({ name, params })),
          }),
          keepalive: true,
        },
      );
    } catch {
      // Analytics is intentionally best effort and must never interrupt app actions.
    }

    if (queue.length > 0) {
      scheduleFlush(0);
    }
  })().finally(() => {
    flushPromise = null;
  });

  return flushPromise;
};

const getRequestDetails = (config?: AxiosRequestConfig) => {
  const method = (config?.method || "GET").toUpperCase();
  const route = normalizeAnalyticsPath(config?.url || "/");
  const matchedAction = ACTION_ROUTES.find(
    (action) => action.method === method && action.pattern.test(route),
  );
  const isMutation = ["POST", "PUT", "PATCH", "DELETE"].includes(method);

  return {
    method,
    route,
    eventName:
      matchedAction?.eventName || (isMutation ? "app_action_completed" : null),
  };
};

export const trackApiAction = (
  config: AxiosRequestConfig | undefined,
  status: number,
  outcome: RequestOutcome,
): void => {
  const request = getRequestDetails(config);
  if (!request.eventName) {
    return;
  }

  trackAppEvent(
    outcome === "success" ? request.eventName : "app_action_failed",
    {
      action_name: request.eventName,
      outcome,
      request_method: request.method.toLowerCase(),
      request_route: request.route,
      response_status: status,
    },
  );
};

const normalizeControlName = (value: string | null): string | null => {
  if (!value || value.includes("@") || /^https?:\/\//i.test(value)) {
    return null;
  }

  const normalized = value
    .trim()
    .toLowerCase()
    .replace(/[0-9a-f]{8}-[0-9a-f-]{27,}/gi, ":id")
    .replace(/[0-9a-f]{20,}/gi, ":id")
    .replace(/\d{6,}/g, ":id")
    .replace(/[^a-z0-9:_/-]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 80);

  return normalized || null;
};

export const trackAnalyticsControl = (element: HTMLElement): void => {
  const href =
    element instanceof HTMLAnchorElement
      ? normalizeAnalyticsPath(element.href)
      : null;
  const controlName = normalizeControlName(
    element.dataset.analyticsAction ||
      element.getAttribute("aria-label") ||
      element.getAttribute("name") ||
      element.id ||
      href,
  );
  if (!controlName) {
    return;
  }

  const requestedEvent = element.dataset.analyticsEvent;
  trackAppEvent(
    requestedEvent && EVENT_NAME_PATTERN.test(requestedEvent)
      ? requestedEvent
      : "ui_control_clicked",
    {
      control_name: controlName,
      control_type:
        element.getAttribute("role") || element.tagName.toLowerCase(),
    },
  );
};
